import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { fetchProductsByHandles } from '@theme/av-product-fetch';

const STORAGE_KEY = 'av_wishlist_handles';
const UPDATE_EVENT = 'av:wishlist-update';

/**
 * Guest wishlist, stored by product handle in localStorage.
 *
 * Server sync: when a customer is logged in, this should also mirror to a
 * customer metafield so the wishlist follows them across devices. That
 * requires writing through the Customer Account API / a backend endpoint,
 * which isn't reachable from theme Liquid or storefront JS alone, so it is
 * not implemented here. For now the wishlist is localStorage-only for
 * every shopper, guest or signed in.
 */
export class AvWishlist {
  /**
   * @returns {string[]}
   */
  static getHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} handle
   * @returns {boolean}
   */
  static has(handle) {
    return this.getHandles().includes(handle);
  }

  /**
   * @param {string} handle
   */
  static toggle(handle) {
    const handles = this.getHandles();
    const index = handles.indexOf(handle);
    const nowSaved = index === -1;

    if (nowSaved) {
      handles.push(handle);
    } else {
      handles.splice(index, 1);
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // Storage unavailable; the toggle still updates the UI for this view.
    }

    document.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { handle, saved: nowSaved } }));
    return nowSaved;
  }
}

/**
 * A heart button that saves/removes a product from the wishlist.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [button]
 *
 * @extends {Component<Refs>}
 */
class AvWishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    document.addEventListener(UPDATE_EVENT, this.#handleExternalUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(UPDATE_EVENT, this.#handleExternalUpdate);
  }

  toggle = () => {
    const handle = this.dataset.productHandle;
    if (!handle) return;
    AvWishlist.toggle(handle);
    this.#syncState();
  };

  /**
   * @param {CustomEvent<{handle: string}>} event
   */
  #handleExternalUpdate = (event) => {
    if (event.detail?.handle === this.dataset.productHandle) this.#syncState();
  };

  #syncState() {
    const handle = this.dataset.productHandle;
    const saved = handle ? AvWishlist.has(handle) : false;
    this.refs.button?.setAttribute('aria-pressed', String(saved));
    this.classList.toggle('av-wishlist-button--saved', saved);
  }
}

if (!customElements.get('av-wishlist-button')) {
  customElements.define('av-wishlist-button', AvWishlistButton);
}

/**
 * Renders the shopper's saved products on the wishlist page.
 *
 * @typedef {object} PageRefs
 * @property {HTMLElement} [grid]
 * @property {HTMLElement} [empty]
 *
 * @extends {Component<PageRefs>}
 */
class AvWishlistPage extends Component {
  async connectedCallback() {
    super.connectedCallback();
    await this.#render();
    document.addEventListener(UPDATE_EVENT, this.#handleUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(UPDATE_EVENT, this.#handleUpdate);
  }

  #handleUpdate = () => {
    this.#render();
  };

  async #render() {
    const { grid, empty } = this.refs;
    if (!grid) return;

    const handles = AvWishlist.getHandles();

    if (handles.length === 0) {
      grid.replaceChildren();
      if (empty) empty.hidden = false;
      return;
    }

    const products = await fetchProductsByHandles(handles);
    if (empty) empty.hidden = products.length > 0;

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    grid.replaceChildren(
      ...products.map((product) => {
        const item = document.createElement('li');
        item.className = 'av-wishlist-page__item';

        const link = document.createElement('a');
        link.className = 'av-wishlist-page__link';
        link.href = `/products/${product.handle}`;

        const image = product.featured_image || product.images?.[0];
        if (image) {
          const img = document.createElement('img');
          img.src = image;
          img.alt = product.title;
          img.loading = 'lazy';
          img.width = 200;
          img.height = 250;
          img.className = 'av-wishlist-page__image';
          link.appendChild(img);
        }

        const title = document.createElement('span');
        title.className = 'av-wishlist-page__title';
        title.textContent = product.title;
        link.appendChild(title);

        const price = document.createElement('span');
        price.className = 'av-wishlist-page__price';
        price.textContent = formatMoney(product.price, moneyFormat, currency);
        link.appendChild(price);
        if (product.compare_at_price > product.price) {
          const compare = document.createElement('s');
          compare.className = 'compare-at-price';
          compare.textContent = formatMoney(product.compare_at_price, moneyFormat, currency);
          price.append(' ', compare);
        }
        if (product.tags?.includes('new')) {
          const badge = document.createElement('span');
          badge.className = 'av-list-new';
          badge.textContent = this.dataset.newBadgeText || 'New';
          link.prepend(badge);
        }

        item.appendChild(link);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'button button-unstyled av-wishlist-page__remove';
        removeButton.textContent = this.dataset.removeText || 'Remove';
        removeButton.addEventListener('click', () => AvWishlist.toggle(product.handle));
        item.appendChild(removeButton);

        return item;
      })
    );
  }
}

if (!customElements.get('av-wishlist-page')) {
  customElements.define('av-wishlist-page', AvWishlistPage);
}

/**
 * Product-page nudge shown when the product is in the shopper's wishlist.
 *
 * @extends {Component}
 */
class AvWishlistNudge extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    document.addEventListener(UPDATE_EVENT, this.#handleWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(UPDATE_EVENT, this.#handleWishlistUpdate);
  }

  #handleWishlistUpdate = (/** @type {CustomEvent<{handle: string}>} */ event) => {
    if (event.detail?.handle === this.dataset.productHandle) this.#syncState();
  };

  #syncState() {
    const handle = this.dataset.productHandle;
    const shouldShow = !!handle && AvWishlist.has(handle);
    this.hidden = !shouldShow;
  }
}

if (!customElements.get('av-wishlist-nudge')) {
  customElements.define('av-wishlist-nudge', AvWishlistNudge);
}
